import { Injectable, Inject, InjectionToken } from '@angular/core';
import { BehaviorSubject, catchError, Observable, of } from 'rxjs';
import { HttpClient } from "@angular/common/http";

export interface Language {
  code: string;
  displayName: string;
}

export interface LanguageCombinations {
  sourceLanguages: Language[];
  targetCombinations: { [sourceCode: string]: Language[] };
}

export let LANGUAGE_DATA: LanguageCombinations = {
  sourceLanguages: [],
  targetCombinations: {},
};

export type LanguageDataStatus = 'Loading' | 'Success' | 'Error';

@Injectable({
  providedIn: 'root'
})
export class LanguageManagerService {

  constructor(
    @Inject(LANGUAGE_COMBINATIONS) private languageData: LanguageCombinations, @Inject('TRANSLATIONS_API_URL') private translationsUrl: string, private http: HttpClient
  ) { }

  private languageLoadStatus = new BehaviorSubject<LanguageDataStatus>('Loading');

  public status$: Observable<LanguageDataStatus> = this.languageLoadStatus.asObservable();

  loadLanguageData(): void {
    this.http.get<LanguageCombinations>(this.translationsUrl + '/v1/languages', this.getHeaders())
      .pipe(
        catchError(this.handleError<LanguageCombinations>('getLanguageData'))
      ).subscribe({
        next: (response) => {
          if (response) {
            this.languageData.sourceLanguages = response.sourceLanguages;
            this.languageData.targetCombinations = response.targetCombinations;

            this.languageData.sourceLanguages.sort((a, b) => {
              return a.displayName.localeCompare(b.displayName);
            });

            this.updateLoadStatus('Success');
          }
        },
      });
  }

  getSourceLanguages(): Observable<Language[]> {
    return of(this.languageData.sourceLanguages);
  }

  getTargetLanguages(sourceCode: string): Language[] {
    const targetLanguages = this.languageData.targetCombinations[sourceCode] || [];
    targetLanguages.sort((a, b) => {
      return a.displayName.localeCompare(b.displayName);
    });
    return targetLanguages;
  }

  updateLoadStatus(newStatus: LanguageDataStatus): void {
    this.languageLoadStatus.next(newStatus);
  }

  handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.error(`${operation} failed:`, error);
      this.updateLoadStatus('Error');
      return of(result as T);
    };
  }

  private getHeaders() {
    return {
      headers: {
        'Content-type': 'application/json'
      }
    }
  }
}

export const LANGUAGE_COMBINATIONS = new InjectionToken<LanguageCombinations>('LANGUAGE_COMBINATIONS', {
  factory: () => LANGUAGE_DATA
});

